import { MarkdownEditor } from '@/components/MarkdownEditor'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useAuth, useCreatePrompt, useProjectPhases } from '@/hooks'
import { canEditPromptPhase } from '@/lib/permissions'
import type { CreatePromptRequest } from '@/types'
import { Check } from 'lucide-react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'sonner'

const labelClass = "text-xs font-bold leading-[160%] tracking-[0.015em] text-foreground"

const NAME_MAX = 256
const DESCRIPTION_MAX = 2000
const CONTENT_MAX = 50000

interface CreatePromptDialogProps {
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CreatePromptDialog({ projectId, open, onOpenChange }: CreatePromptDialogProps) {
  const createPrompt = useCreatePrompt(projectId)
  const { data: projectPhases = [] } = useProjectPhases(projectId)
  const { getProjectRoles } = useAuth(projectId)
  const projectRoles = getProjectRoles(projectId)
  const editableProjectPhases = projectPhases.filter(phase => canEditPromptPhase(phase.name, projectRoles))
  const { register, handleSubmit, reset, control, formState: { errors } } = useForm<CreatePromptRequest>({
    defaultValues: {
      projectPhaseId: '',
      name: '',
      internalName: '',
      description: '',
      content: '',
    },
  })

  const onSubmit = async (data: CreatePromptRequest) => {
    try {
      const name = data.name.trim()
      const internalName = data.internalName.trim()
      const description = data.description?.trim()
      const content = data.content.trim()
      await createPrompt.mutateAsync({
        projectPhaseId: data.projectPhaseId,
        name,
        internalName,
        content,
        ...(description ? { description } : {}),
      })
      toast.success('Prompt created successfully')
      reset()
      onOpenChange(false)
    } catch {
      toast.error('Failed to create prompt')
    }
  }

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      reset()
    }
    onOpenChange(isOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[100vw] w-[100vw] h-[100vh] max-h-[100vh] flex flex-col p-0 rounded-none border-none gap-0">
        <DialogHeader className="px-8 py-4 border-b shrink-0 bg-background">
          <DialogTitle className="text-xl font-bold">Create New Prompt</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 flex flex-col overflow-hidden bg-input-background">
            <div className="max-w-4xl w-full mx-auto py-8 px-6 flex-1 flex flex-col min-h-0">
              <div className="rounded-2xl border border-border bg-card p-8 flex flex-col gap-2 flex-1 min-h-0">
                <div className="space-y-2">
                  <Label htmlFor="projectPhaseId" className={labelClass}>Phase <span className="text-red-500">*</span></Label>
                  <Controller
                    name="projectPhaseId"
                    control={control}
                    rules={{ required: 'Phase is required' }}
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select phase" />
                        </SelectTrigger>
                        <SelectContent>
                          {editableProjectPhases.map((projectPhase) => (
                            <SelectItem key={projectPhase.id} value={projectPhase.id}>
                              {projectPhase.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.projectPhaseId && <p className="text-sm text-destructive">{errors.projectPhaseId.message}</p>}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className={labelClass}>Name <span className="text-red-500">*</span></Label>
                    <Input
                      id="name"
                      {...register('name', {
                        required: 'Name is required',
                        maxLength: { value: NAME_MAX, message: `Name must not exceed ${NAME_MAX} characters` },
                      })}
                      placeholder="Enter prompt name"
                    />
                    {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="internalName" className={labelClass}>Internal Name <span className="text-red-500">*</span></Label>
                    <Input
                      id="internalName"
                      data-testid="prompt-internal-name-input"
                      {...register('internalName', {
                        required: 'Internal Name is required',
                        maxLength: { value: NAME_MAX, message: `Internal Name must not exceed ${NAME_MAX} characters` },
                        pattern: { value: /^[a-z0-9]+(-[a-z0-9]+)*$/, message: 'Must be kebab-case (e.g. "my-prompt-1")' },
                      })}
                      placeholder="Enter internal name"
                    />
                    {errors.internalName && <p className="text-sm text-destructive" role="alert">{errors.internalName.message}</p>}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description" className={labelClass}>Description</Label>
                  <Textarea
                    id="description"
                    {...register('description', {
                      maxLength: { value: DESCRIPTION_MAX, message: `Description must not exceed ${DESCRIPTION_MAX} characters` },
                    })}
                    placeholder="Enter prompt description"
                    rows={4}
                  />
                  {errors.description && <p className="text-sm text-destructive">{errors.description.message}</p>}
                </div>
                <div className="flex flex-col gap-2 flex-1 min-h-0">
                  <Label htmlFor="content" className={labelClass}>Prompt Content <span className="text-red-500">*</span></Label>
                  <Controller
                    name="content"
                    control={control}
                    rules={{
                      required: 'Content is required',
                      maxLength: { value: CONTENT_MAX, message: `Content must not exceed ${CONTENT_MAX} characters` },
                    }}
                    render={({ field }) => (
                      <MarkdownEditor
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Enter prompt content"
                        className="flex-1 flex flex-col min-h-0 [&>div:last-child]:flex-1 [&>div:last-child]:overflow-y-auto"
                      />
                    )}
                  />
                  {errors.content && <p className="text-sm text-destructive">{errors.content.message}</p>}
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createPrompt.isPending}>
                    <Check className="h-4 w-4" />
                    {createPrompt.isPending ? 'Creating...' : 'Create Prompt'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
